// src/pages/admin/news/NewsCreate.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";
import { useTranslation } from "react-i18next";

import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";

export default function NewsCreate() {
  const { t } = useTranslation();
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const validate = () => {
    if (!title.trim()) return "제목을 입력해 주세요.";
    // Quill은 내용이 없어도 `<p><br></p>` 등 HTML이 들어올 수 있어서 태그 제거 후 공백 체크
    const plain = content.replace(/<[^>]+>/g, "").trim();
    if (!plain) return "내용을 입력해 주세요.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      // 🔹 XSS 방지
      const sanitizedContent = DOMPurify.sanitize(content);

      // 🔹 이제 백엔드에 title + content만 보냄
      const payload = {
        title: title.trim(),
        content: sanitizedContent,
      };

      const res = await api.post("/api/news", payload, {
        headers: { "Content-Type": "application/json" },
      });

      const resultCode = res.data?.resultCode;
      if (resultCode === "201" || resultCode === 201 || resultCode === "200") {
        setMessage("뉴스&이벤트이 등록되었습니다.");
        nav("/admin/news/list");
      } else {
        setError(
          res.data?.resultMessage ||
            "뉴스&이벤트 등록에 실패했습니다. 다시 시도해 주세요."
        );
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.resultMessage ||
          "뉴스&이벤트 등록 중 오류가 발생했습니다."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "link",
    "image",
  ];

  return (
    <AdminLayout>
      {/* 헤더 영역 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
          {t("adminNews.createTitle", "뉴스&이벤트 등록")}
        </h1>
        <button
          type="button"
          onClick={() => nav("/admin/news/list")}
          className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          {t("adminNews.toList", "목록으로")}
        </button>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-6 rounded-2xl border bg-white p-6 shadow-sm"
      >
        {/* 제목 */}
        <Field label="제목" required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="제목을 입력해 주세요."
          />
        </Field>

        {/* 내용 (ReactQuill) */}
        <Field label="내용" required>
          <div className="rounded-xl border">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="뉴스&이벤트 내용을 입력해 주세요."
              className="quill-editor"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            간단한 서식, 링크 등을 사용할 수 있습니다.
          </p>
        </Field>

        {/* 에러 / 메시지 */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {message}
          </div>
        )}

        {/* 버튼 */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => nav("/admin/news/list")}
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "저장 중..." : "등록"}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="grid gap-1 text-sm">
      <div className="font-medium text-slate-800">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </div>
      {children}
    </div>
  );
}
